import { toast } from "react-toastify";

import { logout } from "../slices/authSlice";
import { removeUsers } from "../slices/userSlice";
import { removeAttendances } from "../slices/attendanceListSlice";
import { removeSelfUsers } from "../slices/userSelfSlice";
import { clearGymData } from "../slices/gymDataSlice";
import { apiSlice } from "../slices/apiSlice";
import { applyGymSwitch } from "./applyGymSwitch";
import { buildLogoutUserInfo, getGymKey } from "./multiGym";

/**
 * Logs out ONLY the gym that is currently active.
 *
 * With multi-gym login, "Logout" must drop the active session and fall back to
 * the most recently used parked gym; a full logout is correct only when no
 * usable parked session is left. This lives in one place because the desktop
 * and mobile headers previously each had their own copy, and the mobile one
 * had drifted into always doing a full logout.
 *
 * Callers pass the RTK Query triggers in, since hooks cannot be used here.
 */
export const performMultiGymLogout = async ({
  userInfo,
  dispatch,
  navigate,
  logoutApi,
  triggerMyDetails,
}) => {
  // Ask the server to drop this device's token. If that call fails (offline,
  // 5xx, already-expired token) we still continue: otherwise the Logout button
  // would silently do nothing and leave the user stuck in the app.
  try {
    await logoutApi({
      associateId: `${userInfo?.gymId?._id}${userInfo?.associateId}`,
      token: userInfo?.token,
    }).unwrap();
  } catch (err) {
    console.log("Server logout failed, logging out locally anyway", err);
  }

  // Most recently used parked gym first.
  const parked = [...(userInfo?.myGyms || [])].reverse();
  let nextInfo = null;

  for (const gym of parked) {
    try {
      const details = await triggerMyDetails({ token: gym?.token }).unwrap();

      // The server must answer for the SAME gym as the token we sent. A
      // mismatch means request identity is broken, so skip this session
      // rather than mixing two gyms' data.
      if (
        details?.gymId &&
        String(details.gymId._id) !== String(getGymKey(gym))
      ) {
        continue;
      }

      nextInfo = buildLogoutUserInfo(userInfo, getGymKey(gym));
      if (details?.gymId && nextInfo) nextInfo.gymId = details.gymId;
      break;
    } catch (verifyErr) {
      if (verifyErr?.status === 401 || verifyErr?.status === 403) {
        // Parked session expired. It stays in myGyms so the owner can log in
        // to it again from the switcher.
        toast.warning(
          `Session for ${gym?.gymId?.name} has expired, skipping it`
        );
        continue;
      }
      // Network hiccup — switch locally anyway rather than forcing a full
      // logout the user did not ask for.
      nextInfo = buildLogoutUserInfo(userInfo, getGymKey(gym));
      break;
    }
  }

  if (nextInfo) {
    applyGymSwitch(dispatch, nextInfo);
    navigate("/");
    toast.success(
      `Logged out of ${userInfo?.gymId?.name}, switched to ${nextInfo?.gymId?.name}`
    );
    return;
  }

  // Nothing usable left — full logout. Per-gym caches are cleared too so the
  // next account to log in on this device cannot see the previous one's data.
  dispatch(logout());
  dispatch(removeUsers());
  dispatch(removeAttendances());
  dispatch(removeSelfUsers());
  dispatch(clearGymData());
  dispatch(apiSlice.util.resetApiState());
  navigate("/login");
  toast.success("Successfully logged out");
};
